import {
  ApiDashboardProfileResponse,
  ApiProject,
  ApiUserDashboardProfile,
  ApiUserDashboardProfilePayload,
} from "@incendium/api";
import { dashboardService } from "Apis";
import { useProjectSharedState } from "Hooks/useSharedState";
import { useSelectedProject } from "Hooks/useSelectedProject";
import {
  DEFAULT_DASHBOARD_PROFILE,
  DashboardProfile,
  DashboardReportId,
  findProfileById,
  getVisibleReports,
} from "features/dashboard/constants/dashboardReports";
import produce from "immer";
import { useCallback, useMemo, useState } from "react";

function mapApiProfile(profile: ApiDashboardProfileResponse): DashboardProfile {
  return {
    id: String(profile.id),
    name: profile.name || "Untitled",
    description: profile.description,
    reportIds: (profile.reportIds || []) as DashboardReportId[],
  };
}

function resolveActiveProfileId(
  selection: ApiUserDashboardProfile | null | undefined,
  customProfiles: DashboardProfile[],
): string {
  if (selection?.presetKey) {
    const preset = findProfileById(selection.presetKey, []);
    if (preset) {
      return preset.id;
    }
  }

  if (selection?.dashboardProfileId) {
    const customId = String(selection.dashboardProfileId);
    if (findProfileById(customId, customProfiles)) {
      return customId;
    }
  }

  return DEFAULT_DASHBOARD_PROFILE.id;
}

function hasUserSelection(
  selection: ApiUserDashboardProfile | null | undefined,
) {
  return Boolean(selection?.presetKey || selection?.dashboardProfileId);
}

function selectionPayloadForProfile(
  profile: DashboardProfile,
): ApiUserDashboardProfilePayload {
  if (profile.isPreset) {
    return { presetKey: profile.id };
  }
  return { dashboardProfileId: Number(profile.id) };
}

const fetchDashboardProfiles = async (selectedProject: ApiProject | null) => {
  if (!selectedProject?.id) {
    return [];
  }
  const res = await dashboardService.dashboardServiceListDashboardProfiles({
    projectId: selectedProject.id,
  });
  return res.results || [];
};

const fetchUserDashboardProfile = async (
  selectedProject: ApiProject | null,
) => {
  if (!selectedProject?.id) {
    return {};
  }
  return dashboardService.dashboardServiceGetUserDashboardProfile({
    projectId: selectedProject.id,
  });
};

export function useDashboardProfiles() {
  const { selectedProject } = useSelectedProject();
  const projectId = selectedProject?.id;

  const {
    state: profiles,
    setState: setProfiles,
    fetched: profilesFetched,
  } = useProjectSharedState<ApiDashboardProfileResponse[]>(
    "dashboardProfiles",
    [],
    fetchDashboardProfiles,
  );

  const {
    state: selection,
    setState: setSelection,
    fetched: selectionFetched,
  } = useProjectSharedState<ApiUserDashboardProfile>(
    "userDashboardProfile",
    {},
    fetchUserDashboardProfile,
  );

  const [isSaving, setIsSaving] = useState(false);
  const [selectedTabId, setSelectedTabId] = useState<DashboardReportId | null>(
    null,
  );

  const isLoaded = !projectId || (profilesFetched && selectionFetched);

  const customProfiles = useMemo(
    () => (profiles || []).map(mapApiProfile),
    [profiles],
  );

  const activeProfileId = resolveActiveProfileId(selection, customProfiles);

  const activeProfile =
    findProfileById(activeProfileId, customProfiles) ??
    DEFAULT_DASHBOARD_PROFILE;

  const visibleReports = useMemo(
    () => getVisibleReports(activeProfile),
    [activeProfile],
  );

  const activeTabId = useMemo(() => {
    if (
      selectedTabId &&
      visibleReports.some((report) => report.id === selectedTabId)
    ) {
      return selectedTabId;
    }
    return visibleReports[0]?.id ?? DEFAULT_DASHBOARD_PROFILE.reportIds[0];
  }, [selectedTabId, visibleReports]);

  const needsSetup =
    isLoaded && !!projectId && !hasUserSelection(selection);

  const setUserSelection = useCallback(
    async (profile: DashboardProfile) => {
      if (!projectId) {
        return;
      }
      const reports = getVisibleReports(profile);
      setSelectedTabId(
        reports[0]?.id ?? DEFAULT_DASHBOARD_PROFILE.reportIds[0],
      );
      const next =
        await dashboardService.dashboardServiceSetUserDashboardProfile({
          projectId,
          payload: selectionPayloadForProfile(profile),
        });
      setSelection(next);
    },
    [projectId, setSelection],
  );

  const selectProfile = useCallback(
    async (profileId: string) => {
      const profile = findProfileById(profileId, customProfiles);
      if (!profile || !projectId) {
        return;
      }
      setIsSaving(true);
      try {
        await setUserSelection(profile);
      } finally {
        setIsSaving(false);
      }
    },
    [customProfiles, projectId, setUserSelection],
  );

  const applyProfile = useCallback(
    async (profile: DashboardProfile) => {
      if (!projectId) {
        return;
      }

      setIsSaving(true);
      try {
        if (profile.isPreset) {
          await setUserSelection(profile);
          return;
        }

        const payload = {
          name: profile.name,
          description: profile.description,
          reportIds: profile.reportIds,
        };

        const existingId = Number(profile.id);
        const isExistingCustom =
          Number.isFinite(existingId) &&
          existingId > 0 &&
          customProfiles.some((item) => item.id === profile.id);

        let savedApi: ApiDashboardProfileResponse;
        if (isExistingCustom) {
          savedApi =
            await dashboardService.dashboardServiceUpdateDashboardProfile({
              projectId,
              dashboardProfileId: existingId,
              payload,
            });
          setProfiles(
            produce(profiles, (draft) => {
              const idx = draft.findIndex((p) => p.id === savedApi.id);
              if (idx >= 0) {
                draft[idx] = savedApi;
              }
            }),
          );
        } else {
          savedApi =
            await dashboardService.dashboardServiceCreateDashboardProfile({
              projectId,
              payload,
            });
          setProfiles(
            produce(profiles, (draft) => {
              draft.push(savedApi);
            }),
          );
        }

        await setUserSelection(mapApiProfile(savedApi));
      } finally {
        setIsSaving(false);
      }
    },
    [customProfiles, profiles, projectId, setProfiles, setUserSelection],
  );

  const setActiveTabId = useCallback((tabId: DashboardReportId) => {
    setSelectedTabId(tabId);
  }, []);

  return {
    isLoaded,
    isSaving,
    needsSetup,
    activeProfile,
    customProfiles,
    visibleReports,
    activeTabId,
    selectProfile,
    applyProfile,
    setActiveTabId,
  };
}
